import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { showAPI, templateAPI } from '../utils/api';
import api from '../utils/api';
import { Plus, Trash, DotsSixVertical, FloppyDisk, FolderOpen, X, CaretDown, CaretRight } from '@phosphor-icons/react';
import { TshirtBlock, TieOutBlock, ShavingsBlock } from '../components/show/ShowMerchBlocks';
import { toast } from 'sonner';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ProducerContactCard } from '../components/show/ProducerContactCard';

const SortableClassCard = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children(listeners)}
    </div>
  );
};

const ShowCreate = () => {
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: today,
    end_date: today,
    location: '',
    arenas: ['Main Arena'],
    stalls_available: 0,
    stall_price: 0,
    stall_price_per_day: 0,
    stall_per_day_disabled: false,
    online_entries_disabled: false,
    rv_hookups_available: 0,
    rv_hookup_price: 0,
    rv_hookup_price_per_day: 0,
    video_stream_url: '',
    processing_fee: 0,
    processing_fee_type: 'per_day',
    producer_contact_name: '',
    producer_contact_email: '',
    producer_contact_phones: [],
    check_payable_to: '',
    check_mailing_address: '',
    tshirt_enabled: false,
    tshirt_price: 0,
    tshirt_label: 'Show T-Shirt',
    tshirt_sizes: ['S', 'M', 'L', 'XL', 'XXL'],
    tie_out_fee_enabled: false,
    tie_out_fee_price: 0,
    tie_out_fee_label: 'Tie-Out Fee',
    shavings_enabled: false,
    shavings_price_per_bag: 0,
    shavings_label: 'Shavings'
  });
  const [classes, setClasses] = useState([
    { 
      temp_id: 'cls_0',
      name: '', 
      entry_fee: '', 
      payback_percent: 70, 
      added_money: 0, 
      scheduled_day: '', 
      scheduled_date: '',
      division_type: 'NONE',
      division_split: 0.5,
      entry_cap: '',
      class_type: 'standard',
      rollover_from_class_ids: [],
      rollover_category: ''
    }
  ]);
  const [sidepots, setSidepots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeCreateDayTab, setActiveCreateDayTab] = useState('ALL');
  const [showbillUrl, setShowbillUrl] = useState(null);
  // v225 — Point-Awarding Organizations picker. All orgs on the site;
  // producer picks whichever ones should also award their members
  // season points from this show. Selections land in
  // `pending_co_sanctioning_org_ids` on the show doc.
  const [allOrgs, setAllOrgs] = useState([]);
  const [pickedOrgIds, setPickedOrgIds] = useState([]);
  const [orgFilter, setOrgFilter] = useState('');
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [stallsExpanded, setStallsExpanded] = useState(false);
  const [rvExpanded, setRvExpanded] = useState(false);
  const [streamExpanded, setStreamExpanded] = useState(false);
  const [scheduleExpanded, setScheduleExpanded] = useState(false);

  useEffect(() => { loadTemplates(); loadOrgs(); }, []);

  const loadOrgs = async () => {
    try {
      const { data } = await api.get('/organizations/public');
      setAllOrgs(Array.isArray(data) ? data : []);
    } catch { /* non-fatal */ }
  };

  const loadTemplates = async () => {
    try {
      const { data } = await templateAPI.list();
      setTemplates(data);
    } catch { /* ignore if not logged in */ }
  };

  const saveAsTemplate = async () => {
    if (!templateName.trim()) { toast.error('Enter a template name'); return; }
    setSavingTemplate(true);
    try {
      const templateData = {
        arenas: formData.arenas,
        stalls_available: parseInt(formData.stalls_available) || 0,
        stall_price: parseFloat(formData.stall_price) || 0,
        stall_price_per_day: parseFloat(formData.stall_price_per_day) || 0,
        stall_per_day_disabled: !!formData.stall_per_day_disabled,
        online_entries_disabled: !!formData.online_entries_disabled,
        rv_hookups_available: parseInt(formData.rv_hookups_available) || 0,
        rv_hookup_price: parseFloat(formData.rv_hookup_price) || 0,
        rv_hookup_price_per_day: parseFloat(formData.rv_hookup_price_per_day) || 0,
        processing_fee: parseFloat(formData.processing_fee) || 0,
        classes: classes.map(c => ({
          name: c.name, entry_fee: c.entry_fee, payback_percent: c.payback_percent,
          added_money: c.added_money || 0, division_type: c.division_type || 'NONE',
          division_split: c.division_split || 0.5, arena: c.arena || '',
          scheduled_day: c.scheduled_day || '', rollover_from_class_ids: [],
          rollover_category: c.rollover_category || ''
        })),
        sidepots: sidepots.map(s => ({ name: s.name, entry_fee: parseFloat(s.entry_fee), rollover_from_class_ids: s.rollover_from_class_ids || [], division_type: s.division_type || 'NONE', division_split: s.division_type === '2D' ? (parseFloat(s.division_split) || 0.5) : null, payout_pct: parseFloat(s.payout_pct ?? 100), added_money: parseFloat(s.added_money ?? 0), scheduled_day: s.scheduled_day || '' }))
      };
      await templateAPI.save({ template_name: templateName.trim(), template_data: templateData });
      toast.success(`Template "${templateName.trim()}" saved!`);
      setShowSaveDialog(false);
      setTemplateName('');
      loadTemplates();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save template'); }
    finally { setSavingTemplate(false); }
  };

  const applyTemplate = (template) => {
    const d = template.template_data;
    setFormData(prev => ({
      ...prev,
      arenas: d.arenas || prev.arenas,
      stalls_available: d.stalls_available || 0,
      stall_price: d.stall_price || 0,
      stall_price_per_day: d.stall_price_per_day || 0,
      stall_per_day_disabled: !!d.stall_per_day_disabled,
      online_entries_disabled: !!d.online_entries_disabled,
      rv_hookups_available: d.rv_hookups_available || 0,
      rv_hookup_price: d.rv_hookup_price || 0,
      rv_hookup_price_per_day: d.rv_hookup_price_per_day || 0,
      processing_fee: d.processing_fee || 0,
    }));
    if (d.classes && d.classes.length > 0) {
      setClasses(d.classes.map((c, i) => ({
        ...c, temp_id: `tpl_${i}_${Date.now()}`,
        rollover_from_class_ids: [], rollover_category: c.rollover_category || '',
        division_type: c.division_type || 'NONE', division_split: c.division_split || 0.5,
        scheduled_day: c.scheduled_day || '', scheduled_date: ''
      })));
    }
    if (d.sidepots && d.sidepots.length > 0) {
      setSidepots(d.sidepots.map(s => ({
        name: s.name,
        entry_fee: s.entry_fee,
        rollover_from_class_ids: s.rollover_from_class_ids || [],
        division_type: s.division_type || 'NONE',
        division_split: s.division_split ?? 0.5,
        payout_pct: s.payout_pct ?? 100,
        added_money: s.added_money ?? 0,
      })));
    }
    toast.success(`Template "${template.template_name}" applied!`);
  };

  const deleteTemplate = async (id, name) => {
    try {
      await templateAPI.delete(id);
      toast.success(`Template "${name}" deleted`);
      loadTemplates();
    } catch { toast.error('Failed to delete template'); }
  };

  // Generate show days for multi-day events
  const getShowDays = () => {
    if (!formData.start_date || !formData.end_date || formData.start_date === formData.end_date) return [];
    const days = [];
    const [sy, sm, sd] = formData.start_date.split('-').map(Number);
    const [ey, em, ed] = formData.end_date.split('-').map(Number);
    const start = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);
    const current = new Date(start);
    while (current <= end) {
      const dayName = current.toLocaleDateString('en-US', { weekday: 'long' });
      const dateStr = `${current.getFullYear()}-${String(current.getMonth()+1).padStart(2,'0')}-${String(current.getDate()).padStart(2,'0')}`;
      const label = `${dayName} (${current.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`;
      days.push({ value: dayName, dateStr, label });
      current.setDate(current.getDate() + 1);
    }
    return days;
  };
  const showDays = getShowDays();
  const isMultiDay = showDays.length > 0;

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();

    // Excel files: send to backend for parsing
    if (['xlsx', 'xls'].includes(ext)) {
      setLoading(true);
      toast.info('Parsing Excel file...');
      try {
        const formDataObj = new FormData();
        formDataObj.append('file', file);
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${BACKEND_URL}/api/parse-class-file`, {
          method: 'POST',
          body: formDataObj,
          credentials: 'include'
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.detail || 'Failed to parse Excel file');
        }
        const data = await response.json();
        if (data.classes && data.classes.length > 0) {
          setClasses(data.classes.map((c, i) => ({
            ...c,
            temp_id: `cls_excel_${i}_${Date.now()}`,
            rollover_from_class_ids: [],
            rollover_category: '',
            division_type: 'NONE',
            division_split: 0.5,
            scheduled_day: c.scheduled_day || '',
            scheduled_date: ''
          })));
          toast.success(`Imported ${data.classes.length} classes from Excel`);
        } else {
          toast.error('No valid classes found in Excel file');
        }
      } catch (error) {
        toast.error(error.message || 'Failed to parse Excel file');
      } finally {
        setLoading(false);
      }
      e.target.value = '';
      return;
    }

    // CSV/TXT: parse client-side
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      // Skip header row if it exists
      const startIndex = lines[0].toLowerCase().includes('class') || lines[0].toLowerCase().includes('name') ? 1 : 0;
      
      const importedClasses = [];
      for (let i = startIndex; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        // Split by comma or tab
        const parts = line.includes('\t') ? line.split('\t') : line.split(',');
        
        if (parts.length >= 2) {
          importedClasses.push({
            name: parts[0].trim(),
            entry_fee: parseFloat(parts[1].trim()) || 0,
            payback_percent: parts[2] ? parseFloat(parts[2].trim()) : 70,
            added_money: parts[3] ? parseFloat(parts[3].trim()) : 0,
            places_paid: parts[4] ? parseInt(parts[4].trim()) : 4
          });
        }
      }
      
      if (importedClasses.length > 0) {
        setClasses(importedClasses.map((c, i) => ({
          ...c,
          temp_id: `cls_csv_${i}_${Date.now()}`,
          rollover_from_class_ids: [],
          rollover_category: '',
          division_type: 'NONE',
          division_split: 0.5,
          scheduled_day: '',
          scheduled_date: ''
        })));
        toast.success(`Imported ${importedClasses.length} classes`);
      } else {
        toast.error('No valid classes found in file');
      }
    };
    
    reader.readAsText(file);
    e.target.value = ''; // Reset input
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['.pdf', '.jpg', '.jpeg', '.png', '.docx', '.doc', '.xlsx', '.xls', '.txt'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(fileExt)) {
      toast.error('File type not supported. Please upload PDF, Image, Word, Excel, or Text file.');
      return;
    }

    setLoading(true);
    const fileType = fileExt === '.pdf' ? 'PDF' : 
                     ['.jpg', '.jpeg', '.png'].includes(fileExt) ? 'image' :
                     ['.docx', '.doc'].includes(fileExt) ? 'Word document' :
                     ['.xlsx', '.xls'].includes(fileExt) ? 'spreadsheet' : 'document';
    
    toast.info(`🤖 AI is analyzing your ${fileType}... This may take up to 2 minutes for large files`);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Use the shared axios client so the Bearer token comes through the
      // request interceptor and we avoid the iOS Safari PWA bug where
      // `credentials: 'include' + mode: 'cors'` on a multipart upload throws
      // "The object can not be cloned." in standalone mode.
      // - Don't set Content-Type manually — axios injects the multipart boundary.
      // - timeout: 180s lets large flyers (multi-page PDFs) finish AI analysis
      //   before axios aborts with a "Network Error".
      // - withCredentials: false avoids the CORS preflight that some
      //   ingress configs reject for multipart bodies; Bearer token in the
      //   request interceptor is sufficient for auth.
      const { data } = await api.post('/parse-show-document', formData, {
        timeout: 180000,
        withCredentials: false,
      });

      // Auto-fill form with extracted data
      if (data.show_name) {
        setFormData(prev => ({
          ...prev,
          name: data.show_name,
          description: data.description || '',
          start_date: data.start_date || '',
          end_date: data.end_date || '',
          location: data.location || ''
        }));
      }

      // Import classes
      if (data.classes && data.classes.length > 0) {
        // Group classes by day if multi-day show
        let displayMessage = '';
        
        if (data.is_multi_day) {
          const dayGroups = {};
          data.classes.forEach(cls => {
            const day = cls.scheduled_day || 'Unscheduled';
            if (!dayGroups[day]) dayGroups[day] = [];
            dayGroups[day].push(cls);
          });
          
          const dayCount = Object.keys(dayGroups).length;
          displayMessage = `✨ Multi-day show detected! Extracted ${data.classes.length} classes across ${dayCount} days`;
        } else {
          displayMessage = `✨ Successfully extracted show info and ${data.classes.length} classes from ${fileType}!`;
        }
        
        setClasses(data.classes.map((c, i) => ({
          ...c,
          temp_id: c.temp_id || c.id || `cls_import_${i}_${Date.now()}`,
          rollover_from_class_ids: c.rollover_from_class_ids || [],
          rollover_category: c.rollover_category || '',
          division_type: c.division_type || 'NONE',
          division_split: c.division_split || 0.5
        })));
        toast.success(displayMessage);
      } else if (data.show_name) {
        toast.success(`✨ Show info extracted! No classes found in ${fileType}.`);
      } else {
        toast.warning('Could not extract complete information. Please fill in manually.');
      }

      // Save showbill URL if returned
      if (data.showbill_url) {
        setShowbillUrl(data.showbill_url);
      }

    } catch (error) {
      console.error('Document parsing error:', error);
      let detail = error?.response?.data?.detail || error?.message || 'Failed to parse document. Please try manual entry.';
      // Distinguish common axios failure modes so the toast actually helps
      // the producer instead of just saying "Network Error".
      if (error?.code === 'ECONNABORTED' || /timeout/i.test(error?.message || '')) {
        detail = 'AI took too long to analyze this file. Try a smaller PDF/image or fill in classes manually.';
      } else if (error?.message === 'Network Error') {
        detail = 'Could not reach the server. Check your connection and try again — or use a smaller file.';
      }
      toast.error(detail);
    } finally {
      setLoading(false);
      e.target.value = ''; // Reset input
    }
  };

  const downloadTemplate = () => {
    const template = 'Class Name,Entry Fee,Payback %,Added Money,Places Paid\nOpen 4D Barrels,50,70,0,4\nYouth Barrels,30,70,0,4\nSenior Barrels,40,70,100,5';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'classes_template.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const addClass = (dayOverride) => {
    const assignDay = dayOverride || (activeCreateDayTab !== 'ALL' ? activeCreateDayTab : '');
    setClasses([...classes, { 
      temp_id: `cls_${Date.now()}`,
      name: '', 
      entry_fee: '', 
      payback_percent: 70, 
      added_money: 0, 
      scheduled_day: assignDay, 
      scheduled_date: '',
      division_type: 'NONE',
      division_split: 0.5,
      arena: formData.arenas[0] || '',
      rollover_from_class_ids: [],
      rollover_category: ''
    }]);
  };

  const removeClass = (index) => {
    const removedId = classes[index]?.temp_id || classes[index]?.id;
    const updated = classes.filter((_, i) => i !== index).map(c => ({
      ...c,
      rollover_from_class_ids: (c.rollover_from_class_ids || []).filter(id => id !== removedId)
    }));
    setClasses(updated);
  };

  /**
   * Duplicate a class so producers reuse payouts/fees and just rename it.
   * Generates a fresh temp_id, appends " (Copy)", strips rollover targets.
   */
  const duplicateClass = (index) => {
    const source = classes[index];
    if (!source) return;
    const { id: _omit_id, temp_id: _omit_temp, ...rest } = source;
    const copy = {
      ...rest,
      temp_id: `c_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      name: source.name ? `${source.name} (Copy)` : 'New Class (Copy)',
      rollover_from_class_ids: [],
      rollover_category: '',
    };
    const next = [...classes.slice(0, index + 1), copy, ...classes.slice(index + 1)];
    setClasses(next);
  };

  const updateClass = (index, field, value) => {
    setClasses(prev => prev.map((c, i) => i === index ? { ...c, [field]: value } : c));
  };

  const moveClass = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= classes.length) return;
    const updated = [...classes];
    [updated[index], updated[target]] = [updated[target], updated[index]];
    setClasses(updated);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const getClassDndId = (cls, index) => cls.temp_id || cls.id || `cls_idx_${index}`;

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setClasses(prev => {
      const oldIndex = prev.findIndex((c, i) => getClassDndId(c, i) === active.id);
      const newIndex = prev.findIndex((c, i) => getClassDndId(c, i) === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const addSidepot = () => {
    setSidepots([...sidepots, { name: '', entry_fee: '', rollover_from_class_ids: [], division_type: 'NONE', division_split: 0.5, payout_pct: 100, added_money: 0, scheduled_day: '' }]);
  };

  const removeSidepot = (index) => {
    setSidepots(sidepots.filter((_, i) => i !== index));
  };

  const updateSidepot = (index, field, value) => {
    const updated = [...sidepots];
    updated[index][field] = value;
    setSidepots(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const showData = {
        ...formData,
        showbill_url: showbillUrl || null,
        classes: classes.map(c => ({
          temp_id: c.temp_id || c.id || '',
          name: c.name,
          entry_fee: parseFloat(c.entry_fee),
          payback_percent: parseFloat(c.payback_percent),
          added_money: parseFloat(c.added_money) || 0,
          scheduled_day: c.scheduled_day || '',
          scheduled_date: c.scheduled_date || '',
          division_type: c.division_type || 'NONE',
          division_split: c.division_type === '2D' ? parseFloat(c.division_split) : null,
          arena: c.arena || formData.arenas[0] || '',
          rollover_from_class_ids: c.rollover_from_class_ids || [],
          rollover_category: c.rollover_category || null,
          entry_cap: (c.entry_cap === '' || c.entry_cap == null) ? 0 : (parseInt(c.entry_cap, 10) || 0),
          class_type: c.class_type || 'standard',
        })),
        sidepots: sidepots.map(s => ({
          name: s.name,
          entry_fee: parseFloat(s.entry_fee),
          rollover_from_class_ids: s.rollover_from_class_ids || [],
          division_type: s.division_type || 'NONE',
          division_split: s.division_type === '2D' ? (parseFloat(s.division_split) || 0.5) : null,
          payout_pct: parseFloat(s.payout_pct ?? 100),
          added_money: parseFloat(s.added_money ?? 0),
          scheduled_day: s.scheduled_day || '',
          added_money: parseFloat(s.added_money ?? 0),
        })),
        stalls_available: parseInt(formData.stalls_available) || 0,
        stall_price: parseFloat(formData.stall_price) || 0,
        stall_price_per_day: parseFloat(formData.stall_price_per_day) || 0,
        stall_per_day_disabled: !!formData.stall_per_day_disabled,
        online_entries_disabled: !!formData.online_entries_disabled,
        rv_hookups_available: parseInt(formData.rv_hookups_available) || 0,
        rv_hookup_price: parseFloat(formData.rv_hookup_price) || 0,
        rv_hookup_price_per_day: parseFloat(formData.rv_hookup_price_per_day) || 0,
        refund_cutoff_date: formData.refund_cutoff_date || null,
        entry_deadline: formData.entry_deadline || null,
        processing_fee: parseFloat(formData.processing_fee) || 0,
        processing_fee_type: formData.processing_fee_type || 'per_day',
        producer_contact_name: formData.producer_contact_name || null,
        producer_contact_email: formData.producer_contact_email || null,
        producer_contact_phones: formData.producer_contact_phones || [],
        check_payable_to: formData.check_payable_to || null,
        check_mailing_address: formData.check_mailing_address || null,
        tshirt_enabled: !!formData.tshirt_enabled,
        tshirt_price: parseFloat(formData.tshirt_price) || 0,
        tshirt_label: formData.tshirt_label || 'Show T-Shirt',
        tshirt_sizes: formData.tshirt_sizes || [],
        tie_out_fee_enabled: !!formData.tie_out_fee_enabled,
        tie_out_fee_price: parseFloat(formData.tie_out_fee_price) || 0,
        tie_out_fee_label: formData.tie_out_fee_label || 'Tie-Out Fee',
        shavings_enabled: !!formData.shavings_enabled,
        shavings_price_per_bag: parseFloat(formData.shavings_price_per_bag) || 0,
        shavings_label: formData.shavings_label || 'Shavings',
        // v225 — pass the producer's picked org IDs through to the
        // backend where they land in pending_co_sanctioning_org_ids.
        point_awarding_org_ids: pickedOrgIds,
      };

      await showAPI.create(showData);
      toast.success('Show created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create show');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-black text-white mb-2" style={{fontFamily: 'Outfit'}}>Create New Show</h1>
          <p className="text-[#9E9E9E]">Set up your horse show</p>
        </div>

        {/* Template Picker */}
        {templates.length > 0 && (
          <div className="mb-8 bg-[#161616] border border-[#262626] rounded-lg p-5" data-testid="template-picker">
            <div className="flex items-center gap-2 mb-3">
              <FolderOpen size={18} className="text-[#FF9500]" />
              <h3 className="text-white font-bold text-sm" style={{fontFamily: 'Outfit'}}>Use a Saved Template</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {templates.map(t => (
                <div key={t.id} className="group flex items-center gap-1 bg-[#0A0A0A] border border-[#262626] rounded-lg hover:border-[#FF9500]/50 transition-colors">
                  <button
                    type="button"
                    onClick={() => applyTemplate(t)}
                    className="px-3 py-2 text-sm text-white hover:text-[#FF9500] transition-colors"
                    data-testid={`template-apply-${t.id}`}
                  >
                    {t.template_name}
                    <span className="text-[#525252] text-xs ml-1.5">({t.template_data?.classes?.length || 0} classes)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteTemplate(t.id, t.template_name)}
                    className="pr-2 text-[#525252] hover:text-[#FF3B30] transition-colors opacity-0 group-hover:opacity-100"
                    data-testid={`template-delete-${t.id}`}
                    title="Delete template"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} data-testid="create-show-form" className="space-y-8">
          {/* Document Upload Section */}
          <div className="bg-gradient-to-r from-[#0096FF]/10 to-[#00D4FF]/5 border border-[#0096FF]/30 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#0096FF]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">✨</span>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-2" style={{fontFamily: 'Outfit'}}>AI-Powered Quick Setup</h3>
                <p className="text-[#9E9E9E] mb-4 text-sm">Upload your show document and AI will automatically extract the show name, dates, location, and all classes!</p>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.docx,.doc,.xlsx,.xls,.txt"
                  onChange={handlePdfUpload}
                  className="hidden"
                  id="pdf-upload"
                  data-testid="document-upload-input"
                  disabled={loading}
                />
                <label
                  htmlFor="pdf-upload"
                  className={`inline-flex items-center gap-2 px-6 py-3 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-semibold rounded-md transition-colors ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M5.5 13a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 13H11V9.413l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13H5.5z"/>
                  </svg>
                  {loading ? 'Processing...' : 'Upload Show Document'}
                </label>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-[#0096FF]/10 text-[#0096FF] rounded text-xs font-medium">PDF</span>
                  <span className="px-2 py-1 bg-[#00D4FF]/10 text-[#00D4FF] rounded text-xs font-medium">Images (JPG, PNG)</span>
                  <span className="px-2 py-1 bg-[#C0C0C0]/10 text-[#C0C0C0] rounded text-xs font-medium">Word (.docx)</span>
                  <span className="px-2 py-1 bg-[#34C759]/10 text-[#34C759] rounded text-xs font-medium">Excel (.xlsx)</span>
                  <span className="px-2 py-1 bg-[#9E9E9E]/10 text-[#9E9E9E] rounded text-xs font-medium">Text</span>
                </div>
                <p className="text-xs text-[#9E9E9E] mt-2">AI extracts: Show name • Dates • Location • All classes with fees</p>
                {showbillUrl && (
                  <div className="mt-3 rounded-lg overflow-hidden border border-[#34C759]/30 bg-[#0A0A0A]">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${showbillUrl}`} alt="Showbill preview" className="w-full max-h-48 object-contain" data-testid="showbill-preview" />
                    <p className="text-center text-[10px] text-[#34C759] py-1">Showbill saved — will display on show page</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div className="bg-[#161616] border border-[#262626] rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white mb-6" style={{fontFamily: 'Outfit'}}>Basic Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Show Name</label>
                <input
                  data-testid="show-name-input"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white placeholder-[#525252] focus:outline-none focus:border-[#0096FF] transition-colors"
                  placeholder="Enter show name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Description (Optional)</label>
                <textarea
                  data-testid="show-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white placeholder-[#525252] focus:outline-none focus:border-[#0096FF] transition-colors"
                  placeholder="Describe your show (optional)"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Start Date</label>
                  <input
                    data-testid="show-start-date-input"
                    type="date"
                    required
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#F8F8F8] mb-2">End Date</label>
                  <input
                    data-testid="show-end-date-input"
                    type="date"
                    required
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Location</label>
                <input
                  data-testid="show-location-input"
                  type="text"
                  required
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white placeholder-[#525252] focus:outline-none focus:border-[#0096FF] transition-colors"
                  placeholder="Show location"
                />
              </div>

              {/* Arenas */}
              <div>
                <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Arenas</label>
                <div className="space-y-2">
                  {formData.arenas.map((arena, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input
                        type="text"
                        value={arena}
                        data-testid={`arena-input-${idx}`}
                        onChange={(e) => {
                          const updated = [...formData.arenas];
                          updated[idx] = e.target.value;
                          setFormData({...formData, arenas: updated});
                        }}
                        className="flex-1 px-4 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white placeholder-[#525252] focus:outline-none focus:border-[#0096FF] transition-colors"
                        placeholder="Arena name"
                      />
                      {formData.arenas.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, arenas: formData.arenas.filter((_, i) => i !== idx)})}
                          className="px-3 py-2 text-[#FF3B30] hover:text-[#FF3B30]/80"
                        >
                          <Trash size={18} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    data-testid="add-arena-btn"
                    onClick={() => setFormData({...formData, arenas: [...formData.arenas, `Arena ${formData.arenas.length + 1}`]})}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-[#0096FF] hover:text-[#00D4FF] transition-colors"
                  >
                    <Plus size={14} weight="bold" /> Add Arena
                  </button>
                </div>
                <p className="text-xs text-[#9E9E9E] mt-1">Define arenas to assign classes to specific locations</p>
              </div>

              {/* T-Shirt merch — sold with pre-entries */}
              <TshirtBlock formData={formData} setFormData={setFormData} testPrefix="create" />

              <div className="border border-[#262626] rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setStallsExpanded(s => !s)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-[#0A0A0A] hover:bg-[#161616] transition-colors"
                  data-testid="show-stalls-toggle"
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-[#F8F8F8]">
                    {stallsExpanded ? <CaretDown size={14} /> : <CaretRight size={14} />}
                    Stalls
                    {(parseInt(formData.stalls_available) || 0) > 0 && (
                      <span className="text-[10px] text-[#0096FF] bg-[#0096FF]/10 px-2 py-0.5 rounded-full">
                        {formData.stalls_available} available
                      </span>
                    )}
                  </span>
                  <span className="text-[10px] text-[#555]">
                    {stallsExpanded ? 'Click to collapse' : 'Click to configure'}
                  </span>
                </button>
                {stallsExpanded && (
                  <div className="p-4 grid grid-cols-2 gap-4 border-t border-[#262626] bg-[#0A0A0A]/50">
                    <div>
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Stalls Available</label>
                      <input
                        data-testid="show-stalls-input"
                        type="number"
                        min="0"
                        value={formData.stalls_available}
                        onChange={(e) => setFormData({...formData, stalls_available: e.target.value})}
                        className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Stall Price - Weekend ($)</label>
                      <input
                        data-testid="show-stall-price-input"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.stall_price}
                        onChange={(e) => setFormData({...formData, stall_price: e.target.value})}
                        className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Stall Price - Per Day ($)</label>
                      <input
                        data-testid="show-stall-price-perday-input"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.stall_price_per_day}
                        onChange={(e) => setFormData({...formData, stall_price_per_day: e.target.value})}
                        className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                        placeholder="0.00"
                      />
                      <p className="text-[10px] text-[#555] mt-1">Set both to let riders choose</p>
                      <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid="create-stall-per-day-disabled-label">
                        <input type="checkbox"
                          checked={!!formData.stall_per_day_disabled}
                          onChange={(e) => setFormData({...formData, stall_per_day_disabled: e.target.checked})}
                          className="w-3.5 h-3.5 accent-[#FF9500]"
                          data-testid="create-stall-per-day-disabled" />
                        <span className="text-[11px] text-[#9E9E9E]">Disable per-day stall pricing</span>
                      </label>
                    </div>
                    <div className="col-span-2 mt-1 px-3 py-2 bg-[#0096FF]/5 border border-[#0096FF]/20 rounded-md">
                      <p className="text-[11px] text-[#9E9E9E]">
                        <span className="text-[#0096FF] font-semibold">Tip:</span> Stall maps, charts, and barn layouts are configured on the <span className="text-[#F8F8F8] font-medium">Edit Show</span> page after you save this show — look for "Stall Chart Builder" and "Facility Map Upload".
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="border border-[#262626] rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setRvExpanded(s => !s)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-[#0A0A0A] hover:bg-[#161616] transition-colors"
                  data-testid="show-rv-toggle"
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-[#F8F8F8]">
                    {rvExpanded ? <CaretDown size={14} /> : <CaretRight size={14} />}
                    RV Hookups
                    {(parseInt(formData.rv_hookups_available) || 0) > 0 && (
                      <span className="text-[10px] text-[#0096FF] bg-[#0096FF]/10 px-2 py-0.5 rounded-full">
                        {formData.rv_hookups_available} available
                      </span>
                    )}
                  </span>
                  <span className="text-[10px] text-[#555]">
                    {rvExpanded ? 'Click to collapse' : 'Click to configure'}
                  </span>
                </button>
                {rvExpanded && (
                  <div className="p-4 grid grid-cols-2 gap-4 border-t border-[#262626] bg-[#0A0A0A]/50">
                    <div>
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">RV Hookups</label>
                      <input
                        data-testid="show-rv-input"
                        type="number"
                        min="0"
                        value={formData.rv_hookups_available}
                        onChange={(e) => setFormData({...formData, rv_hookups_available: e.target.value})}
                        className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">RV Hookup Price - Weekend ($)</label>
                      <input
                        data-testid="show-rv-price-input"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.rv_hookup_price}
                        onChange={(e) => setFormData({...formData, rv_hookup_price: e.target.value})}
                        className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">RV Hookup Price - Per Day ($)</label>
                      <input
                        data-testid="show-rv-price-perday-input"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.rv_hookup_price_per_day}
                        onChange={(e) => setFormData({...formData, rv_hookup_price_per_day: e.target.value})}
                        className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                        placeholder="0.00"
                      />
                      <p className="text-[10px] text-[#555] mt-1">Set both to let riders choose</p>
                    </div>
                    <div className="col-span-2 mt-1 px-3 py-2 bg-[#0096FF]/5 border border-[#0096FF]/20 rounded-md">
                      <p className="text-[11px] text-[#9E9E9E]">
                        <span className="text-[#0096FF] font-semibold">Tip:</span> RV maps, charts, and lot layouts are configured on the <span className="text-[#F8F8F8] font-medium">Edit Show</span> page after you save this show — look for "RV Chart Builder" and "Facility Map Upload".
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Tie-Out Fee — sits right below stall/RV pricing per user request */}
              <TieOutBlock formData={formData} setFormData={setFormData} testPrefix="create" />

              {/* Shavings/Sawdust — sold by the bag */}
              <ShavingsBlock formData={formData} setFormData={setFormData} testPrefix="create" />

              <div className="border border-[#262626] rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setStreamExpanded(s => !s)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-[#0A0A0A] hover:bg-[#161616] transition-colors"
                  data-testid="show-stream-toggle"
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-[#F8F8F8]">
                    {streamExpanded ? <CaretDown size={14} /> : <CaretRight size={14} />}
                    Live Stream URL <span className="text-[10px] text-[#9E9E9E] font-normal">(Optional)</span>
                    {formData.video_stream_url && (
                      <span className="text-[10px] text-[#34C759] bg-[#34C759]/10 px-2 py-0.5 rounded-full">Set</span>
                    )}
                  </span>
                  <span className="text-[10px] text-[#555]">
                    {streamExpanded ? 'Click to collapse' : 'Click to configure'}
                  </span>
                </button>
                {streamExpanded && (
                  <div className="p-4 border-t border-[#262626] bg-[#0A0A0A]/50">
                    <input
                      data-testid="show-video-url-input"
                      type="url"
                      value={formData.video_stream_url}
                      onChange={(e) => setFormData({...formData, video_stream_url: e.target.value})}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white placeholder-[#525252] focus:outline-none focus:border-[#0096FF] transition-colors"
                      placeholder="https://www.youtube.com/embed/VIDEO_ID or stream URL"
                    />
                    <p className="text-xs text-[#9E9E9E] mt-2">
                      Embed a live video stream on the public results page (YouTube, Vimeo, or custom stream URL)
                    </p>
                  </div>
                )}
              </div>

              <div className="border border-[#262626] rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setScheduleExpanded(s => !s)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-[#0A0A0A] hover:bg-[#161616] transition-colors"
                  data-testid="show-schedule-toggle"
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-[#F8F8F8]">
                    {scheduleExpanded ? <CaretDown size={14} /> : <CaretRight size={14} />}
                    Schedule &amp; Fees
                    {(formData.refund_cutoff_date || formData.entry_deadline || (parseFloat(formData.processing_fee) || 0) > 0) && (
                      <span className="text-[10px] text-[#34C759] bg-[#34C759]/10 px-2 py-0.5 rounded-full">Configured</span>
                    )}
                  </span>
                  <span className="text-[10px] text-[#555]">
                    {scheduleExpanded ? 'Click to collapse' : 'Click to configure'}
                  </span>
                </button>
                {scheduleExpanded && (
                  <div className="p-4 space-y-4 border-t border-[#262626] bg-[#0A0A0A]/50">
                    <div>
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Refund Cutoff Date</label>
                      <input
                        data-testid="show-refund-cutoff-input"
                        type="date"
                        value={formData.refund_cutoff_date || ''}
                        onChange={(e) => setFormData({...formData, refund_cutoff_date: e.target.value})}
                        className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                      />
                      <p className="text-xs text-[#9E9E9E] mt-1">Riders can request refunds before this date. Defaults to the show start date if not set.</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Online Entry Deadline</label>
                      <input
                        data-testid="show-entry-deadline-input"
                        type="datetime-local"
                        value={formData.entry_deadline || ''}
                        onChange={(e) => setFormData({...formData, entry_deadline: e.target.value})}
                        className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                      />
                      <p className="text-xs text-[#9E9E9E] mt-1">Online entries will close at this date and time. Leave blank for no deadline.</p>
                      <label className="flex items-center gap-2 mt-3 cursor-pointer" data-testid="create-online-entries-disabled-label">
                        <input
                          type="checkbox"
                          checked={!!formData.online_entries_disabled}
                          onChange={(e) => setFormData({...formData, online_entries_disabled: e.target.checked})}
                          className="w-3.5 h-3.5 accent-[#FF9500]"
                          data-testid="create-online-entries-disabled"
                        />
                        <span className="text-[11px] text-[#9E9E9E]">
                          Disable online entries — riders must download the printable form and mail it in
                        </span>
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Office/Rider Fee ($)</label>
                      <input
                        data-testid="show-processing-fee-input"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.processing_fee}
                        onChange={(e) => setFormData({...formData, processing_fee: e.target.value})}
                        className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                        placeholder="0.00"
                      />
                      <p className="text-xs text-[#9E9E9E] mt-1">Flat fee added per entry transaction (common in barrel races). Leave at 0 for no office/rider fee.</p>
                      <div className="mt-3 flex gap-2" data-testid="processing-fee-type-toggle">
                        <button type="button" onClick={() => setFormData({...formData, processing_fee_type: 'weekend'})}
                          className={`flex-1 px-3 py-2 rounded-md text-xs font-semibold transition-colors ${(formData.processing_fee_type || 'per_day') === 'weekend' ? 'bg-[#0096FF] text-white' : 'bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] hover:border-[#0096FF]/50'}`}
                          data-testid="processing-fee-type-weekend">
                          Weekend (flat, once per rider)
                        </button>
                        <button type="button" onClick={() => setFormData({...formData, processing_fee_type: 'per_day'})}
                          className={`flex-1 px-3 py-2 rounded-md text-xs font-semibold transition-colors ${(formData.processing_fee_type || 'per_day') === 'per_day' ? 'bg-[#0096FF] text-white' : 'bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] hover:border-[#0096FF]/50'}`}
                          data-testid="processing-fee-type-per-day">
                          Per Day
                        </button>
                      </div>
                      <p className="text-[10px] text-[#555] mt-1">Weekend: charged once per rider regardless of how many days/horses they enter. Per Day: charged once per rider per show day.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Point-Awarding Organizations (v225).
              Producer picks which orgs will have their members earn
              season points from this show. Each selected org's admin
              must approve the co-sanction before their standings
              start counting (v217 flow). */}
          <div className="bg-[#161616] border border-[#262626] rounded-lg p-6" data-testid="point-awarding-orgs-section">
            <h2 className="text-2xl font-bold text-white mb-1" style={{fontFamily: 'Outfit'}}>Point-Awarding Organizations</h2>
            <p className="text-xs text-[#9E9E9E] mb-4">
              Pick any organizations whose members should earn season points from this show.
              Each selected org&apos;s admin will get a co-sanction approval request. The system
              uses each org&apos;s membership roster to match rider names → points.
            </p>
            {allOrgs.length === 0 ? (
              <p className="text-[#555] text-xs italic">No organizations available yet.</p>
            ) : (
              <>
                {/* Live filter — narrows the checkbox grid as producer types. */}
                <div className="mb-3">
                  <input
                    type="text"
                    value={orgFilter}
                    onChange={(e) => setOrgFilter(e.target.value)}
                    placeholder="Filter orgs..."
                    className="w-full sm:w-64 px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded text-sm text-white placeholder-[#555] focus:border-[#00D4FF]/50 focus:outline-none"
                    data-testid="point-awarding-org-filter"
                  />
                </div>
                {(() => {
                  const q = orgFilter.trim().toLowerCase();
                  // Show picked orgs first so they never disappear when the
                  // filter no longer matches — producer can still uncheck.
                  const visible = allOrgs.filter((o) =>
                    !q || (o.name || '').toLowerCase().includes(q) || pickedOrgIds.includes(o.id)
                  );
                  if (visible.length === 0) {
                    return <p className="text-[#555] text-xs italic">No orgs match &ldquo;{orgFilter}&rdquo;.</p>;
                  }
                  return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-56 overflow-y-auto pr-1">
                      {visible.map((o) => {
                        const checked = pickedOrgIds.includes(o.id);
                        return (
                          <label
                            key={o.id}
                            className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer text-sm transition-colors ${
                              checked
                                ? 'bg-[#00D4FF]/10 border-[#00D4FF]/40 text-white'
                                : 'bg-[#0A0A0A] border-[#262626] text-[#9E9E9E] hover:border-[#00D4FF]/30 hover:text-white'
                            }`}
                            data-testid={`point-awarding-org-${o.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                setPickedOrgIds((prev) =>
                                  e.target.checked
                                    ? [...prev, o.id]
                                    : prev.filter((x) => x !== o.id)
                                );
                              }}
                              className="accent-[#00D4FF]"
                              data-testid={`point-awarding-org-checkbox-${o.id}`}
                            />
                            <span className="truncate">{o.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  );
                })()}
              </>
            )}
            {pickedOrgIds.length > 0 && (
              <p className="text-[10px] text-[#00D4FF] mt-3" data-testid="point-awarding-count">
                {pickedOrgIds.length} organization{pickedOrgIds.length > 1 ? 's' : ''} will receive a co-sanction request.
              </p>
            )}
          </div>

          {/* Producer Contact */}
          <ProducerContactCard
            value={formData}
            onChange={(patch) => setFormData({ ...formData, ...patch })}
            testIdPrefix="create-producer-contact"
          />

          {/* Classes */}
          <div className="bg-[#161616] border border-[#262626] rounded-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white" style={{fontFamily: 'Outfit'}}>Classes</h2>
              <div className="flex gap-2">
                <input
                  type="file"
                  accept=".csv,.txt,.xlsx,.xls"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="class-upload"
                  data-testid="class-upload-input"
                />
                <label
                  htmlFor="class-upload"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-transparent border border-[#00D4FF] hover:bg-[#00D4FF]/10 text-[#00D4FF] font-semibold rounded-md transition-colors text-sm cursor-pointer"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 1a.5.5 0 0 1 .5.5v6h6a.5.5 0 0 1 0 1h-6v6a.5.5 0 0 1-1 0v-6h-6a.5.5 0 0 1 0-1h6v-6A.5.5 0 0 1 8 1z"/>
                  </svg>
                  Upload CSV / Excel
                </label>
                <button
                  type="button"
                  onClick={downloadTemplate}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-transparent border border-[#9E9E9E] hover:bg-[#9E9E9E]/10 text-[#9E9E9E] font-semibold rounded-md transition-colors text-sm"
                >
                  Download Template
                </button>
                <button
                  type="button"
                  data-testid="add-class-btn"
                  onClick={() => addClass()}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-semibold rounded-md transition-colors text-sm"
                >
                  <Plus size={16} weight="bold" />
                  {isMultiDay && activeCreateDayTab !== 'ALL' && activeCreateDayTab !== 'UNSCHEDULED' ? `Add to ${activeCreateDayTab}` : 'Add Class'}
                </button>
              </div>
            </div>
            
            {/* Upload Instructions */}
            <div className="mb-4 p-4 bg-[#0096FF]/5 border border-[#0096FF]/20 rounded-lg">
              <p className="text-sm text-[#9E9E9E] mb-2">
                <span className="text-[#0096FF] font-semibold">💡 Tip:</span> Upload a CSV or Excel file with your classes, or use the AI upload above for PDFs, images, and documents.
              </p>
              <p className="text-xs text-[#9E9E9E]">
                Format: Class Name, Entry Fee, Payback %, Added Money, Places Paid
              </p>
            </div>
            <div className="space-y-4">
              {/* Day Tabs for multi-day shows */}
              {isMultiDay && classes.length > 0 && (() => {
                const usedDays = [...new Set(classes.map(c => c.scheduled_day).filter(Boolean))];
                const hasUnscheduled = classes.some(c => !c.scheduled_day);
                const hasTabs = usedDays.length > 0;
                if (!hasTabs) return null;
                return (
                  <div className="flex gap-1 mb-2 bg-[#0A0A0A] rounded-lg p-1 overflow-x-auto" data-testid="create-day-tabs">
                    <button
                      type="button"
                      onClick={() => setActiveCreateDayTab('ALL')}
                      className={`flex-shrink-0 px-4 py-2.5 rounded-md text-sm font-bold transition-all ${
                        activeCreateDayTab === 'ALL' ? 'bg-[#0096FF] text-white' : 'text-[#9E9E9E] hover:text-white hover:bg-[#262626]'
                      }`}
                      data-testid="create-day-tab-all"
                    >
                      All
                      <span className={`ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-black ${
                        activeCreateDayTab === 'ALL' ? 'bg-white/25 text-white' : 'bg-[#262626] text-[#9E9E9E]'
                      }`}>{classes.length}</span>
                    </button>
                    {showDays.map(d => {
                      const count = classes.filter(c => c.scheduled_day === d.value).length;
                      if (count === 0 && !usedDays.includes(d.value)) return null;
                      return (
                        <button
                          type="button"
                          key={d.value}
                          onClick={() => setActiveCreateDayTab(d.value)}
                          className={`flex-shrink-0 px-4 py-2.5 rounded-md text-sm font-bold transition-all ${
                            activeCreateDayTab === d.value ? 'bg-[#0096FF] text-white' : 'text-[#9E9E9E] hover:text-white hover:bg-[#262626]'
                          }`}
                          data-testid={`create-day-tab-${d.value}`}
                        >
                          {d.value}
                          <span className={`ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-black ${
                            activeCreateDayTab === d.value ? 'bg-white/25 text-white' : 'bg-[#262626] text-[#9E9E9E]'
                          }`}>{count}</span>
                        </button>
                      );
                    })}
                    {hasUnscheduled && (
                      <button
                        type="button"
                        onClick={() => setActiveCreateDayTab('UNSCHEDULED')}
                        className={`flex-shrink-0 px-4 py-2.5 rounded-md text-sm font-bold transition-all ${
                          activeCreateDayTab === 'UNSCHEDULED' ? 'bg-[#FF9500] text-white' : 'text-[#9E9E9E] hover:text-white hover:bg-[#262626]'
                        }`}
                        data-testid="create-day-tab-unscheduled"
                      >
                        Unassigned
                        <span className={`ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-black ${
                          activeCreateDayTab === 'UNSCHEDULED' ? 'bg-white/25 text-white' : 'bg-[#262626] text-[#9E9E9E]'
                        }`}>{classes.filter(c => !c.scheduled_day).length}</span>
                      </button>
                    )}
                  </div>
                );
              })()}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext
                items={classes.map((cls, i) => getClassDndId(cls, i)).filter((_, i) => {
                  if (!isMultiDay || activeCreateDayTab === 'ALL') return true;
                  const cls = classes[i];
                  if (activeCreateDayTab === 'UNSCHEDULED') return !cls.scheduled_day;
                  return cls.scheduled_day === activeCreateDayTab;
                })}
                strategy={verticalListSortingStrategy}
              >
              {classes.map((cls, index) => {
                // Filter by active day tab
                if (isMultiDay && activeCreateDayTab !== 'ALL') {
                  if (activeCreateDayTab === 'UNSCHEDULED') {
                    if (cls.scheduled_day) return null;
                  } else {
                    if (cls.scheduled_day !== activeCreateDayTab) return null;
                  }
                }
                const dndId = getClassDndId(cls, index);
                return (
                <SortableClassCard id={dndId} key={dndId}>
                  {(listeners) => (
                <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-4">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-3">
                      <div {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-[#525252] hover:text-[#9E9E9E] transition-colors touch-none" data-testid={`class-drag-handle-${index}`} title="Drag to reorder">
                        <DotsSixVertical size={20} weight="bold" />
                      </div>
                      <h3 className="text-white font-semibold">Class {index + 1}</h3>
                      {cls.scheduled_day && (
                        <span className="px-3 py-1 bg-[#0096FF]/20 text-[#0096FF] rounded-full text-xs font-semibold">
                          {cls.scheduled_day}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => duplicateClass(index)}
                        className="px-2 py-1 text-[#0096FF] hover:text-[#00D4FF] hover:bg-[#0096FF]/10 rounded text-xs font-semibold transition-colors"
                        data-testid={`create-duplicate-class-${index}`}
                        title="Duplicate this class — keeps fees, payouts, division settings"
                      >
                        Duplicate
                      </button>
                      {classes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeClass(index)}
                          className="text-[#FF3B30] hover:text-[#FF3B30]/80 px-1"
                          data-testid={`create-remove-class-${index}`}
                        >
                          <Trash size={20} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Class Name</label>
                      <input
                        type="text"
                        required
                        value={cls.name}
                        onChange={(e) => updateClass(index, 'name', e.target.value)}
                        className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white placeholder-[#525252] focus:outline-none focus:border-[#0096FF] transition-colors"
                        placeholder="e.g., Open 4D Barrels"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Entry Fee ($)</label>
                      <input
                        type="number"
                        required
                        step="0.01"
                        value={cls.entry_fee}
                        onChange={(e) => updateClass(index, 'entry_fee', e.target.value)}
                        className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Payback %</label>
                      <input
                        type="number"
                        required
                        value={cls.payback_percent}
                        onChange={(e) => updateClass(index, 'payback_percent', e.target.value)}
                        className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Added Money ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={cls.added_money}
                        onChange={(e) => updateClass(index, 'added_money', e.target.value)}
                        data-testid={`class-added-money-${index}`}
                        placeholder="0"
                        className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">
                        Class Type
                      </label>
                      <select
                        value={cls.class_type || 'standard'}
                        onChange={(e) => updateClass(index, 'class_type', e.target.value)}
                        data-testid={`class-type-${index}`}
                        className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                      >
                        <option value="standard">Standard (with payouts &amp; placings)</option>
                        <option value="warmup">Warm-up (no payout, unlimited entries)</option>
                        <option value="exhibition">Exhibition (no payout, unlimited entries)</option>
                      </select>
                      <p className="text-xs text-[#9E9E9E] mt-1">
                        Warm-up &amp; exhibition classes skip duplicate-entry checks and entry caps so riders can run multiple times for practice.
                      </p>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">
                        Entry Cap <span className="text-[#9E9E9E] text-xs font-normal">(Optional — leave blank for no limit)</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={cls.entry_cap || ''}
                        onChange={(e) => updateClass(index, 'entry_cap', e.target.value)}
                        data-testid={`class-entry-cap-${index}`}
                        placeholder="e.g., 50"
                        className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#FACC15] transition-colors"
                      />
                      <p className="text-xs text-[#9E9E9E] mt-1">
                        Once this many riders enter, the class is closed to new entries (rollovers still allowed).
                      </p>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Arena</label>
                      <select
                        value={cls.arena || formData.arenas[0] || ''}
                        onChange={(e) => updateClass(index, 'arena', e.target.value)}
                        className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                        data-testid={`class-arena-select-${index}`}
                      >
                        {formData.arenas.map(arena => (
                          <option key={arena} value={arena}>{arena}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">
                        Division Type (Optional - Can set later)
                      </label>
                      <select
                        value={cls.division_type || 'NONE'}
                        onChange={(e) => updateClass(index, 'division_type', e.target.value)}
                        className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                      >
                        <option value="NONE">Not Set (Decide Later)</option>
                        <option value="4D">4D (0.5s, 1.0s, 2.0s splits)</option>
                        <option value="3D">3D (1.0s, 2.0s splits)</option>
                        <option value="2D">2D (Custom split)</option>
                        <option value="OVERALL">No Divisions (Overall Only)</option>
                      </select>
                      <p className="text-xs text-[#9E9E9E] mt-1">
                        You can configure this later based on entry count
                      </p>
                    </div>
                    {cls.division_type === '2D' && (
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-[#F8F8F8] mb-2">2D Split Time</label>
                        <select
                          value={cls.division_split || 0.5}
                          onChange={(e) => updateClass(index, 'division_split', parseFloat(e.target.value))}
                          className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                        >
                          <option value="0.5">0.5 seconds</option>
                          <option value="1.0">1.0 seconds</option>
                        </select>
                      </div>
                    )}
                    {isMultiDay && (
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Scheduled Day *</label>
                        <select
                          value={cls.scheduled_day}
                          onChange={(e) => updateClass(index, 'scheduled_day', e.target.value)}
                          className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                          data-testid={`class-day-select-${index}`}
                        >
                          <option value="">Select Day</option>
                          {showDays.map(d => (
                            <option key={d.value} value={d.value}>{d.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {/* Rollover Configuration */}
                    <div className="col-span-2 border-t border-[#262626] pt-3 mt-1">
                      <label className="block text-sm font-medium text-[#FF9500] mb-2">Rollover From (Optional)</label>
                      <p className="text-[10px] text-[#555] mb-2">Select one or more source classes whose entries roll into this class</p>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto" data-testid={`class-rollover-from-${index}`}>
                        {classes.filter((_, i) => i !== index).map(c => {
                          const cId = c.temp_id || c.id;
                          const selected = (cls.rollover_from_class_ids || []).includes(cId);
                          return (
                            <label key={cId} className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors ${selected ? 'bg-[#FF9500]/10 border border-[#FF9500]/30' : 'bg-[#161616] border border-[#262626] hover:border-[#FF9500]/20'}`}>
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => {
                                  const current = cls.rollover_from_class_ids || [];
                                  const next = selected ? current.filter(id => id !== cId) : [...current, cId];
                                  updateClass(index, 'rollover_from_class_ids', next);
                                }}
                                className="accent-[#FF9500]"
                                data-testid={`class-rollover-check-${index}-${cId}`}
                              />
                              <span className="text-white text-sm">{c.name || `Class ${classes.indexOf(c) + 1}`}</span>
                            </label>
                          );
                        })}
                      </div>
                      {(cls.rollover_from_class_ids || []).length > 0 && (
                        <div className="mt-2">
                          <select
                            value={cls.rollover_category || ''}
                            onChange={(e) => updateClass(index, 'rollover_category', e.target.value)}
                            className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#FF9500] transition-colors"
                            data-testid={`class-rollover-category-${index}`}
                          >
                            <option value="">All Entries</option>
                            <option value="YOUTH">Youth Only</option>
                            <option value="ADULT">Adult Only</option>
                            <option value="MASTERS">Masters Only</option>
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                  )}
                </SortableClassCard>
                );
              })}
              </SortableContext>
            </DndContext>
            </div>
          </div>
          <div className="bg-[#161616] border border-[#262626] rounded-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white" style={{fontFamily: 'Outfit'}}>Sidepots</h2>
                <p className="text-xs text-[#9E9E9E] mt-1">Optional additional buy-ins riders can enter alongside their main class</p>
              </div>
              <button
                type="button"
                data-testid="add-sidepot-btn"
                onClick={addSidepot}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF9500] hover:bg-[#FF9500]/80 text-white font-semibold rounded-md transition-colors text-sm"
              >
                <Plus size={16} weight="bold" /> Add Sidepot
              </button>
            </div>
            {sidepots.length === 0 ? (
              <p className="text-[#9E9E9E] text-sm">No sidepots configured. Add sidepots for additional rider buy-ins.</p>
            ) : (
              <div className="space-y-3">
                {sidepots.map((sp, index) => (
                  <div key={index} className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-4" data-testid={`sidepot-${index}`}>
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-white font-semibold">Sidepot {index + 1}</h3>
                      <button type="button" onClick={() => removeSidepot(index)} className="text-[#FF3B30] hover:text-[#FF3B30]/80" data-testid={`remove-sidepot-${index}`}>
                        <Trash size={18} />
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Name</label>
                        <input type="text" required value={sp.name}
                          onChange={(e) => updateSidepot(index, 'name', e.target.value)}
                          className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                          placeholder="e.g., Youth Sidepot"
                          data-testid={`sidepot-name-${index}`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Fee ($)</label>
                        <input type="number" required step="0.01" value={sp.entry_fee}
                          onChange={(e) => updateSidepot(index, 'entry_fee', e.target.value)}
                          className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                          data-testid={`sidepot-fee-${index}`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Rolls Over From</label>
                        <div
                          className="flex flex-wrap gap-1.5 px-3 py-2 bg-[#161616] border border-[#262626] rounded-md min-h-[42px]"
                          data-testid={`sidepot-rollover-${index}`}
                        >
                          {classes.length === 0 ? (
                            <span className="text-xs text-[#555]">Add a class first</span>
                          ) : classes.map((c, ci) => {
                            const cid = c.temp_id || c.id;
                            if (!cid || !c.name) return null;
                            const selected = (sp.rollover_from_class_ids || []).includes(cid);
                            return (
                              <button
                                key={cid}
                                type="button"
                                onClick={() => {
                                  const cur = new Set(sp.rollover_from_class_ids || []);
                                  if (cur.has(cid)) cur.delete(cid); else cur.add(cid);
                                  updateSidepot(index, 'rollover_from_class_ids', Array.from(cur));
                                }}
                                className={`px-2 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                                  selected
                                    ? 'bg-[#0096FF] text-white'
                                    : 'bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] hover:text-white'
                                }`}
                                data-testid={`sidepot-${index}-class-toggle-${ci}`}
                              >
                                {c.name}
                              </button>
                            );
                          })}
                        </div>
                        <p className="text-[10px] text-[#555] mt-1">Riders entering any selected class can opt in to this sidepot.</p>
                      </div>
                    </div>

                    {/* Advanced sidepot config — mirrors classes */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 pt-3 border-t border-[#262626]">
                      <div>
                        <label className="block text-xs font-medium text-[#F8F8F8] mb-1">Division Type</label>
                        <select
                          value={sp.division_type || 'NONE'}
                          onChange={(e) => updateSidepot(index, 'division_type', e.target.value)}
                          className="w-full px-3 py-1.5 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500]"
                          data-testid={`sidepot-division-type-${index}`}
                        >
                          <option value="NONE">None (Overall)</option>
                          <option value="2D">2D</option>
                          <option value="3D">3D</option>
                          <option value="4D">4D</option>
                        </select>
                      </div>
                      {sp.division_type === '2D' && (
                        <div>
                          <label className="block text-xs font-medium text-[#F8F8F8] mb-1">2D Split (sec)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={sp.division_split ?? 0.5}
                            onChange={(e) => updateSidepot(index, 'division_split', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-1.5 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500]"
                            data-testid={`sidepot-2d-split-${index}`}
                            placeholder="0.5"
                          />
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-medium text-[#F8F8F8] mb-1">Payout %</label>
                        <input
                          type="number"
                          step="1"
                          min="0"
                          max="100"
                          value={sp.payout_pct ?? 100}
                          onChange={(e) => updateSidepot(index, 'payout_pct', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-1.5 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500]"
                          data-testid={`sidepot-payout-pct-${index}`}
                          placeholder="100"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#F8F8F8] mb-1">Added Money ($)</label>
                        <input
                          type="number"
                          step="1"
                          min="0"
                          value={sp.added_money ?? 0}
                          onChange={(e) => updateSidepot(index, 'added_money', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-1.5 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500]"
                          data-testid={`sidepot-added-money-${index}`}
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#F8F8F8] mb-1">Scheduled Day</label>
                        <select
                          value={sp.scheduled_day || ''}
                          onChange={(e) => updateSidepot(index, 'scheduled_day', e.target.value)}
                          className="w-full px-3 py-1.5 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500]"
                          data-testid={`sidepot-scheduled-day-${index}`}
                        >
                          <option value="">Any day</option>
                          {['Friday','Saturday','Sunday','Monday','Tuesday','Wednesday','Thursday'].map(d => (
                            <option key={d} value={d}>{d}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Save as Template + Submit */}
          <div className="space-y-4">
            {/* Save as Template */}
            {!showSaveDialog ? (
              <button
                type="button"
                onClick={() => setShowSaveDialog(true)}
                className="w-full py-3 bg-[#161616] border border-[#FF9500]/30 hover:border-[#FF9500] text-[#FF9500] font-semibold rounded-md transition-colors inline-flex items-center justify-center gap-2 text-sm"
                data-testid="save-template-btn"
              >
                <FloppyDisk size={18} weight="bold" /> Save as Template
              </button>
            ) : (
              <div className="bg-[#161616] border border-[#FF9500]/30 rounded-lg p-4" data-testid="save-template-dialog">
                <p className="text-white text-sm font-semibold mb-3">Name this template</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="e.g., Weekend Barrel Race"
                    className="flex-1 px-4 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500] transition-colors placeholder-[#525252]"
                    data-testid="template-name-input"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), saveAsTemplate())}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={saveAsTemplate}
                    disabled={savingTemplate}
                    className="px-4 py-2.5 bg-[#FF9500] hover:bg-[#FF9500]/80 text-white font-semibold rounded-md text-sm transition-colors disabled:opacity-50"
                    data-testid="template-save-confirm"
                  >
                    {savingTemplate ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowSaveDialog(false); setTemplateName(''); }}
                    className="px-3 py-2.5 text-[#9E9E9E] hover:text-white transition-colors"
                    data-testid="template-save-cancel"
                  >
                    <X size={18} />
                  </button>
                </div>
                <p className="text-[#525252] text-xs mt-2">Saves classes, sidepots, arenas, and pricing. Dates and show name are not included.</p>
              </div>
            )}

            {/* Submit Row */}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 bg-transparent border border-[#262626] hover:border-[#0096FF] text-white rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                data-testid="submit-show-btn"
                type="submit"
                disabled={loading}
                className="flex-1 px-6 py-3 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-semibold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating Show...' : 'Create Show'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Floating Add Class Button */}
      <button
        type="button"
        onClick={() => addClass()}
        className="fixed bottom-6 right-6 z-50 inline-flex items-center gap-2 px-5 py-3 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-bold rounded-full shadow-lg shadow-[#0096FF]/30 transition-all hover:scale-105"
        data-testid="floating-add-class-btn"
      >
        <Plus size={18} weight="bold" /> Add Class
      </button>
    </div>
  );
};

export default ShowCreate;
